import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';
import { BacklogConfirmDialog } from '../shared/BacklogConfirmDialog';
import { isIOSNative } from '../../utils/nativeBridge';
import { useAuth } from '../../App';
import {
  claimBacklogModal,
  releaseBacklogModal,
  subscribeBacklogModal,
  getBacklogModalHolder,
} from './backlogModalMutex';

/**
 * iter1207 - Global Backlog Fee Confirmation modal.
 *
 * Mounted once at the top of DashboardLayout so it appears on ANY page.
 * Polls /plaid/pending-backlogs and /mono/pending-backlogs; when a pending
 * backlog is found, shows BacklogConfirmDialog.
 *
 * • Confirm: POST /plaid/confirm-backlog/{id} (or /mono/confirm-backlog)
 * • Decline ("Not now"): POST /banking/outstanding-tasks/backlog-skip so
 *   the user can resume from the Bank Account card later.
 * • On successful pay: dispatch a window event so pages/panels can refresh.
 */

const REFRESH_EVENT = 'akountfly:backlog-refresh';
const POLL_INTERVAL_MS = 60_000; // gentle: every 60s

export default function GlobalBacklogModalProvider() {
  // iOS App Store build: fee-confirmation UI must never render in-app.
  // Jun 2026 · Paid-choice businesses gave charge consent at onboarding,
  // so on iOS we confirm pending backlogs AUTOMATICALLY (no modal). The
  // backend charges the card; declines surface via in-app notification,
  // push and email.
  if (isIOSNative()) return <IOSAutoBacklogConfirm />;
  return <GlobalBacklogModalProviderInner />;
}

function IOSAutoBacklogConfirm() {
  const { user } = useAuth() || {};
  const attemptedRef = useRef(new Set()); // connection_ids attempted this session

  const autoConfirm = useCallback(async () => {
    try {
      const [plaidRes, monoRes] = await Promise.allSettled([
        apiClient.get('/plaid/pending-backlogs'),
        apiClient.get('/mono/pending-backlogs'),
      ]);
      const items = [];
      if (plaidRes.status === 'fulfilled') {
        (plaidRes.value.data?.pending_backlogs || []).forEach((p) => items.push({ ...p, provider: 'plaid' }));
      }
      if (monoRes.status === 'fulfilled') {
        (monoRes.value.data?.pending_backlogs || []).forEach((m) => items.push({ ...m, provider: 'mono' }));
      }
      for (const it of items) {
        if (attemptedRef.current.has(it.connection_id)) continue;
        attemptedRef.current.add(it.connection_id);
        try {
          const res = it.provider === 'plaid'
            ? await apiClient.post(`/plaid/confirm-backlog/${it.connection_id}`, {})
            : await apiClient.post('/mono/confirm-backlog', { bank_account_id: it.bank_account_id });
          const data = res.data || {};
          if ((data.success && data.payment_succeeded) || data.already_settled || data.success) {
            toast.success('Backlog confirmed — your historical transactions are importing in the background.');
            try { window.dispatchEvent(new CustomEvent(REFRESH_EVENT)); } catch (_) { /* noop */ }
          } else if (data.payment_failed) {
            toast.error('Backlog fee payment failed. The import was cancelled — please update your card, then reopen the app.');
          }
        } catch (_) { /* backend notifies on failure */ }
      }
    } catch { /* retry next tick */ }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    autoConfirm();
    const interval = setInterval(autoConfirm, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, autoConfirm]);

  return null;
}

function GlobalBacklogModalProviderInner() {
  const { user } = useAuth() || {};
  const [pending, setPending] = useState(null); // active pending backlog being shown
  const [queue, setQueue] = useState([]);        // remaining pending backlogs
  const [confirming, setConfirming] = useState(false);
  const [paymentFailure, setPaymentFailure] = useState(null);
  const skippedRef = useRef(new Set()); // connection_ids user dismissed this session

  const fetchPending = useCallback(async () => {
    try {
      const [plaidRes, monoRes] = await Promise.allSettled([
        apiClient.get('/plaid/pending-backlogs'),
        apiClient.get('/mono/pending-backlogs'),
      ]);
      const items = [];
      if (plaidRes.status === 'fulfilled') {
        (plaidRes.value.data?.pending_backlogs || []).forEach((p) => {
          items.push({ ...p, provider: 'plaid' });
        });
      }
      if (monoRes.status === 'fulfilled') {
        (monoRes.value.data?.pending_backlogs || []).forEach((m) => {
          items.push({ ...m, provider: 'mono' });
        });
      }
      // Filter out anything the user already dismissed this session.
      const fresh = items.filter((it) => !skippedRef.current.has(it.connection_id));
      if (fresh.length === 0) {
        setPending(null);
        setQueue([]);
        return;
      }
      // Only replace `pending` if we don't already have one shown, so we
      // don't yank the dialog out from under a user mid-action.
      setPending((prev) => prev || fresh[0]);
      setQueue(fresh.slice(1));
    } catch {
      /* non-fatal; retry next tick */
    }
  }, []);

  // Poll while user is authenticated.
  useEffect(() => {
    if (!user) return undefined;
    fetchPending();
    const interval = setInterval(fetchPending, POLL_INTERVAL_MS);
    // Refresh right after another component signals a change.
    const onBus = () => fetchPending();
    window.addEventListener(REFRESH_EVENT, onBus);
    return () => {
      clearInterval(interval);
      window.removeEventListener(REFRESH_EVENT, onBus);
    };
  }, [user, fetchPending]);

  const advance = useCallback(() => {
    setPaymentFailure(null);
    setPending(queue[0] || null);
    setQueue((q) => q.slice(1));
  }, [queue]);

  const handleConfirm = useCallback(async () => {
    if (!pending) return;
    setConfirming(true);
    setPaymentFailure(null);
    try {
      let res;
      if (pending.provider === 'plaid') {
        res = await apiClient.post(
          `/plaid/confirm-backlog/${pending.connection_id}`,
          {},
        );
      } else {
        res = await apiClient.post('/mono/confirm-backlog', {
          bank_account_id: pending.bank_account_id,
        });
      }
      const data = res.data || {};
      if ((data.success && data.payment_succeeded) || data.already_settled) {
        const processingAsync = data.processing_in_background === true;
        if (data.waived || data.backlog_fee_waived) {
          toast.success(
            processingAsync
              ? "No charge (fee below provider minimum). Historical transactions are posting in the background."
              : "Historical transactions posted. No charge - fee was below the payment processor minimum."
          );
        } else {
          toast.success(
            processingAsync
              ? `${pending.symbol}${(pending.backlog_fee || 0).toFixed(2)} charged. Historical transactions are posting in the background.`
              : `${pending.symbol}${(pending.backlog_fee || 0).toFixed(2)} charged. Historical transactions posted.`
          );
        }
        // Broadcast so the Banks page can re-fetch its tiles.
        try { window.dispatchEvent(new CustomEvent(REFRESH_EVENT)); } catch (_) { /* noop */ }
        skippedRef.current.delete(pending.connection_id);
        advance();
      } else if (data.payment_failed) {
        setPaymentFailure({
          reason: data.reason,
          total_fee: data.total_fee,
          currency: data.currency,
          backlog_tx_count: data.backlog_tx_count,
          can_retry: data.can_retry,
        });
      } else if (data.posting_error) {
        toast.warning('Payment succeeded but transaction posting hit an error. Support notified.');
        advance();
      }
    } catch (err) {
      setPaymentFailure({
        reason: err.response?.data?.detail || 'Unexpected error during payment',
        can_retry: true,
      });
    } finally {
      setConfirming(false);
    }
  }, [pending, advance]);

  const handleDecline = useCallback(async () => {
    if (!pending) return;
    // Record as an outstanding task so the user can retry from the account card.
    try {
      await apiClient.post('/banking/outstanding-tasks/backlog-skip', {
        provider: pending.provider,
        connection_id: pending.connection_id,
        bank_account_id: pending.bank_account_id || null,
      });
    } catch (_) { /* non-fatal */ }
    skippedRef.current.add(pending.connection_id);
    toast.info('Saved. Retry the backlog confirmation later from the bank account tile.');
    advance();
  }, [pending, advance]);

  const handleRetryLater = useCallback(async () => {
    if (!pending) return;
    try {
      await apiClient.post('/banking/outstanding-tasks/backlog-skip', {
        provider: pending.provider,
        connection_id: pending.connection_id,
        bank_account_id: pending.bank_account_id || null,
      });
    } catch (_) { /* non-fatal */ }
    skippedRef.current.add(pending.connection_id);
    setPaymentFailure(null);
    advance();
  }, [pending, advance]);

  // Feb 2026 · Cross-provider modal mutex — GlobalUploadBacklogModalProvider
  // renders a visually identical "Backlog processing fee" dialog for
  // manual PDF uploads. When both had pending fees, the two dialogs
  // stacked, hence "the backlog fee modal is duplicated". This mutex
  // yields to whichever provider claimed first, and re-renders when
  // the holder releases.
  const [mutexHolder, setMutexHolder] = useState(() => getBacklogModalHolder());
  useEffect(() => subscribeBacklogModal(setMutexHolder), []);
  useEffect(() => {
    if (pending) {
      const ok = claimBacklogModal('openbanking');
      if (!ok) return undefined; // upload dialog is holding; wait
      return () => releaseBacklogModal('openbanking');
    }
    return undefined;
  }, [pending]);

  if (!pending) return null;
  if (mutexHolder && mutexHolder !== 'openbanking') return null;

  const preview = !paymentFailure ? {
    transaction_count: pending.backlog_tx_count,
    per_transaction_rate: pending.backlog_per_tx,
    total_fee: pending.backlog_fee,
    currency: pending.backlog_currency,
    symbol: pending.symbol,
    institution_name: pending.institution_name,
    message: pending.backlog_message,
    fee_waived: pending.backlog_fee_waived,
    provider_minimum: pending.provider_minimum,
    provider: pending.provider,
    // Pricing V2 tier-aware fields (Feb 2026) - surfaced when the
    // connection was written with the new fee shape.
    backlog_free_available: pending.backlog_free_available,
    backlog_free_consumed: pending.backlog_free_consumed,
    backlog_chargeable: pending.backlog_chargeable,
    backlog_minimum_fee: pending.backlog_minimum_fee,
    // iter1238 Phase 8.13n — 3-bucket breakdown fields
    backlog_excluded_bank_charge_count: pending.backlog_excluded_bank_charge_count,
    backlog_post_registration_count: pending.backlog_post_registration_count,
    backlog_window_start: pending.backlog_window_start,
    backlog_window_end: pending.backlog_window_end,
  } : null;

  return (
    <BacklogConfirmDialog
      backlogPreview={preview}
      confirming={confirming}
      onConfirm={handleConfirm}
      onDecline={handleDecline}
      paymentFailure={paymentFailure}
      onRetryNow={handleConfirm}
      onRetryLater={handleRetryLater}
      retrying={confirming}
      rollingBack={false}
    />
  );
}

// Convenience helper for other components to force an immediate refresh
// (e.g. after a per-account "Retry" click).
export function triggerBacklogRefresh() {
  try { window.dispatchEvent(new CustomEvent(REFRESH_EVENT)); } catch (_) { /* noop */ }
}
